import * as React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Avatar from '@mui/material/Avatar';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import SendIcon from '@mui/icons-material/Send';
import DeleteIcon from '@mui/icons-material/Delete';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import ClearIcon from '@mui/icons-material/Clear';
import { useTheme } from '@mui/material/styles';

import wsClient from '../services/ws';
import AppConfig from '../services/app-config';
import AuthService from '../services/auth.js';
import { isReservedTeam } from '../services/systemTeams';
import { optimizeImage, optimizeFile } from '../utils/fileOptimize';

function formatEquipoLabel(equipo) {
  const teams = Array.isArray(equipo) ? equipo : (equipo ? [equipo] : []);
  const filtered = teams.filter((t) => !isReservedTeam(t));
  if (filtered.length === 0) return null;
  if (filtered.length === 1) return filtered[0];
  return `${filtered[0]} +${filtered.length - 1}`;
}

// Stable identity for a comment — used for React keys and WS dedupe.
// Prefers a server id; falls back to a composite of fecha|usuario|comentario|
// first attachment path so the same comment always resolves to the same key.
function commentIdentity(c) {
  if (!c || typeof c !== 'object') return String(c);
  if (c.comment_id || c.id) return `id:${c.comment_id || c.id}`;
  const firstAdj = Array.isArray(c.adjuntos) && c.adjuntos.length > 0 ? c.adjuntos[0] : '';
  return `${c.fecha}|${c.usuario}|${c.comentario || ''}|${firstAdj}`;
}

// Reusable live-updating comment thread for gestiones and averias.
// Subscribes to wsClient events for the given entity and renders the
// comment list + input. All colors come from the MUI theme palette
// (dark/light safe). The parent owns the submit/delete API calls.
//
// Props:
//   gestionId | issueId  — entity id (one of)
//   type                — "gestion" | "issue"
//   comments            — current comments array
//   onCommentsChange    — (updater) => void; called with a function or array
//   onEntityUpdate      — (payload) => void; called on *_update events
//   estados             — state definitions map (for rendering state labels)
//   canComment          — bool
//   onSubmitComment     — ({ text, image, files }) => void
//   onDeleteComment     — (comment) => void
//   allowedFileTypes    — [mime,...] for file validation (null = no check)
//   maxFileMb           — number for file size validation (null = no check)
//   currentUser         — current user object (reserved for future use)
//   imageMode           — bool; true = single-image attach (averias style),
//                         false = multi-file attach (gestiones style)
//   imageBaseUrl        — base URL for issue comment images (imageMode only)
//   onOpenImage         — (src) => void for full-size image dialog (imageMode)

// Animated typing dots — each dot has a staggered delay
const typingDotSx = (index) => ({
  display: 'inline-block',
  width: 5,
  height: 5,
  borderRadius: '50%',
  backgroundColor: 'text.secondary',
  animation: `livethread-typing 1.2s ease-in-out ${index * 0.15}s infinite`,
});

export default function LiveThread({
  gestionId,
  issueId,
  type,
  comments,
  onCommentsChange,
  onEntityUpdate,
  estados,
  canComment,
  onSubmitComment,
  onDeleteComment,
  allowedFileTypes,
  maxFileMb,
  currentUser,
  imageMode = false,
  imageBaseUrl,
  onOpenImage,
  variant,
  scrollContainerRef,
}) {
  const entityId = gestionId || issueId;
  const commentEvent = type === 'gestion' ? 'gestion_comment' : 'issue_comment';
  const updateEvent = type === 'gestion' ? 'gestion_update' : 'issue_update';
  const isChat = variant === 'chat';

  const [text, setText] = React.useState('');
  const [image, setImage] = React.useState(null); // base64 (imageMode)
  const [imagePreview, setImagePreview] = React.useState(null);
  // Outstanding imagePreview object URL — tracked in a ref so it can be
  // revoked on unmount regardless of which render created it.
  const imagePreviewUrlRef = React.useRef(null);
  // In-flight submit guard — prevents a fast double-click from firing two
  // identical POSTs while the parent's onSubmitComment promise is pending.
  const submittingRef = React.useRef(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [files, setFiles] = React.useState([]); // File[] (multi-file mode)
  const [fileError, setFileError] = React.useState('');
  // Number of picked-file batches still being optimized. Submit is blocked
  // while > 0 so pending attachments are never silently dropped.
  const [optimizing, setOptimizing] = React.useState(0);
  const [connected, setConnected] = React.useState(
    wsClient && wsClient.status === 'open'
  );
  const [typingUser, setTypingUser] = React.useState(null); // { nombre, username }
  const [highlightIndex, setHighlightIndex] = React.useState(null);
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const listRef = React.useRef(null);
  const highlightTimerRef = React.useRef(null);
  // Chat scroll continuity: the parent (GestionChat) owns the scrollable
  // container; we only auto-scroll on initial load, after our own comment,
  // or for incoming comments while the user is near the bottom.
  const list = Array.isArray(comments) ? comments : [];
  const myEmail = AuthService.getCurrentUserEmail();
  const wasNearBottomRef = React.useRef(true);
  const prevCountRef = React.useRef(0);

  React.useEffect(() => {
    if (!isChat) return undefined;
    const el = scrollContainerRef && scrollContainerRef.current;
    if (!el) return undefined;
    const onScroll = () => {
      wasNearBottomRef.current = (el.scrollHeight - el.scrollTop - el.clientHeight) < 80;
    };
    onScroll();
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [isChat, scrollContainerRef]);

  React.useEffect(() => {
    if (!isChat) return;
    const el = scrollContainerRef && scrollContainerRef.current;
    const prev = prevCountRef.current;
    prevCountRef.current = list.length;
    if (!el || list.length <= prev) return;
    const scrollToBottom = () => { el.scrollTop = el.scrollHeight; };
    const last = list[list.length - 1];
    const appendedOwn = !!(last && myEmail && last.usuario === myEmail);
    if (prev === 0 || appendedOwn || wasNearBottomRef.current) {
      scrollToBottom();
      wasNearBottomRef.current = true;
    }
    // Otherwise the user is reading history — never yank them to the bottom.
  }, [isChat, list, myEmail, scrollContainerRef]);

  // Revoke any outstanding preview object URL on unmount.
  React.useEffect(() => () => {
    if (imagePreviewUrlRef.current) {
      URL.revokeObjectURL(imagePreviewUrlRef.current);
      imagePreviewUrlRef.current = null;
    }
  }, []);

  // --- WebSocket connection tracking + refetch-on-reconnect ---
  const prevConnectedRef = React.useRef(null);
  const onEntityUpdateRef = React.useRef(onEntityUpdate);
  onEntityUpdateRef.current = onEntityUpdate;
  React.useEffect(() => {
    if (!wsClient || typeof wsClient.on !== 'function') return undefined;
    const tick = () => {
      const isOpen = wsClient.status === 'open';
      setConnected(isOpen);
      if (prevConnectedRef.current !== null && isOpen && !prevConnectedRef.current && typeof onEntityUpdateRef.current === 'function') {
        onEntityUpdateRef.current({ refetch: true });
      }
      prevConnectedRef.current = isOpen;
    };
    tick();
    const interval = setInterval(tick, 2000);
    return () => clearInterval(interval);
  }, []);

  // --- Typing indicator: receive events from other users ---
  const typingTimeoutRef = React.useRef(null);
  const typingEvent = type === 'gestion' ? 'gestion_typing' : 'issue_typing';
  React.useEffect(() => {
    if (!wsClient || typeof wsClient.on !== 'function' || !entityId) return undefined;
    const offTyping = wsClient.on(typingEvent, (msg) => {
      const p = msg && (msg.data || msg);
      if (!p || String(p.id) !== String(entityId)) return;
      // Don't show our own typing indicator
      const myEmail = AuthService.getCurrentUserEmail();
      if (p.username && myEmail && p.username === myEmail) return;
      if (p.typing === false) {
        setTypingUser(null);
        if (typingTimeoutRef.current) {
          clearTimeout(typingTimeoutRef.current);
          typingTimeoutRef.current = null;
        }
      } else {
        setTypingUser({ nombre: p.nombre || 'Alguien', username: p.username || '' });
        // Auto-clear after 4s without updates
        if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
        typingTimeoutRef.current = setTimeout(() => {
          setTypingUser(null);
          typingTimeoutRef.current = null;
        }, 4000);
      }
    });
    return () => {
      if (typeof offTyping === 'function') offTyping();
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
        typingTimeoutRef.current = null;
      }
    };
  }, [entityId, typingEvent]);

  // --- Typing indicator: send events to other users (throttled) ---
  const lastTypingSentRef = React.useRef(0);
  const stopTypingTimerRef = React.useRef(null);
  const sendTyping = React.useCallback((isTyping) => {
    if (!wsClient || !entityId) return;
    const now = Date.now();
    if (isTyping) {
      // Throttle: send at most once every 2s
      if (now - lastTypingSentRef.current < 2000) return;
      lastTypingSentRef.current = now;
    }
    const nombre = AuthService.getCurrentUser() || 'Alguien';
    wsClient.send({
      type: 'typing',
      entity_type: type,
      entity_id: entityId,
      nombre,
      typing: isTyping,
    });
  }, [wsClient, entityId, type]);

  const handleTextChange = React.useCallback((e) => {
    const newVal = e.target.value;
    setText(newVal);
    if (newVal.trim()) {
      sendTyping(true);
      // Schedule stop-typing after 3s of no changes
      if (stopTypingTimerRef.current) clearTimeout(stopTypingTimerRef.current);
      stopTypingTimerRef.current = setTimeout(() => {
        sendTyping(false);
        lastTypingSentRef.current = 0;
      }, 3000);
    } else {
      // Empty input = stopped typing
      if (stopTypingTimerRef.current) {
        clearTimeout(stopTypingTimerRef.current);
        stopTypingTimerRef.current = null;
      }
      sendTyping(false);
      lastTypingSentRef.current = 0;
    }
  }, [sendTyping]);

  // Subscribe to wsClient events filtered by entity id.
  const onCommentsChangeRef = React.useRef(onCommentsChange);
  onCommentsChangeRef.current = onCommentsChange;
  React.useEffect(() => {
    if (!wsClient || typeof wsClient.on !== 'function' || !entityId) return undefined;
    const matches = (msg) => {
      const p = msg && (msg.data || msg);
      if (!p) return false;
      return String(p.id) === String(entityId);
    };
    const offComment = wsClient.on(commentEvent, (msg) => {
      const p = msg && (msg.data || msg);
      if (!matches(msg)) return;
      // Clear typing indicator when a comment arrives
      setTypingUser(null);
      if (p && p.comentario && typeof onCommentsChangeRef.current === 'function') {
        onCommentsChangeRef.current((prev) => {
          const arr = Array.isArray(prev) ? prev : [];
          if (arr.some((c) => commentIdentity(c) === commentIdentity(p.comentario))) {
            return arr;
          }
          return [...arr, p.comentario];
        });
      } else if (typeof onEntityUpdateRef.current === 'function') {
        onEntityUpdateRef.current({ refetch: true });
      }
    });
    const offUpdate = wsClient.on(updateEvent, (msg) => {
      if (!matches(msg)) return;
      if (typeof onEntityUpdateRef.current === 'function') {
        const p = msg && (msg.data || msg);
        onEntityUpdateRef.current(p || {});
      }
    });
    return () => {
      if (typeof offComment === 'function') offComment();
      if (typeof offUpdate === 'function') offUpdate();
    };
  }, [entityId, commentEvent, updateEvent]);

  // --- Focus-if-open: when a foreground push arrives for THIS ticket while the
  // user is already viewing it, InAppNots suppresses the snackbar and dispatches
  // mgo:focus-latest-comment. Scroll the comment list to the newest comment and
  // briefly highlight it (theme-aware). The websocket handler above already
  // appended the comment to the list. ---
  React.useEffect(() => {
    if (!entityId) return undefined;
    const handleFocus = (event) => {
      const detail = event && event.detail;
      if (!detail) return;
      const expectedKind = type === 'gestion' ? 'gestion' : 'issue';
      if (detail.kind && detail.kind !== expectedKind) return;
      if (detail.id && String(detail.id) !== String(entityId)) return;

      // Defer to next tick so the just-appended comment is in the DOM.
      const trigger = () => {
        const container = listRef.current;
        if (!container) return;
        const items = container.querySelectorAll('[data-livethread-comment]');
        if (!items.length) return;
        const last = items[items.length - 1];
        if (typeof last.scrollIntoView === 'function') {
          last.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        setHighlightIndex(items.length - 1);
        if (highlightTimerRef.current) {
          clearTimeout(highlightTimerRef.current);
        }
        highlightTimerRef.current = setTimeout(() => {
          setHighlightIndex(null);
          highlightTimerRef.current = null;
        }, 2500);
      };
      // Use rAF + setTimeout so React has flushed the new comment.
      requestAnimationFrame(() => setTimeout(trigger, 0));
    };

    window.addEventListener('mgo:focus-latest-comment', handleFocus);
    return () => {
      window.removeEventListener('mgo:focus-latest-comment', handleFocus);
      if (highlightTimerRef.current) {
        clearTimeout(highlightTimerRef.current);
        highlightTimerRef.current = null;
      }
    };
  }, [entityId, type]);

  const resizeFile = async (file) => {
    const optimized = await optimizeImage(file, { maxWidth: 1200, maxHeight: 1200, quality: 0.8 });
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(optimized);
    });
  };

  const fileTypeOk = (f) => {
    if (!allowedFileTypes || allowedFileTypes.length === 0) return true;
    return allowedFileTypes.includes(f.type);
  };
  const fileSizeOk = (f) => {
    if (!maxFileMb) return true;
    return f.size <= maxFileMb * 1024 * 1024;
  };

  const handleImageAttach = async (event) => {
    const file = event.target.files && event.target.files[0];
    // Reset the input so re-picking the same image fires onChange again.
    event.target.value = '';
    if (!file) return;
    if (!fileTypeOk(file)) {
      setFileError(`Tipo no permitido: ${file.type || file.name}`);
      return;
    }
    if (!fileSizeOk(file)) {
      setFileError(`Archivo excede el tamaño máximo de ${maxFileMb}MB`);
      return;
    }
    setFileError('');
    try {
      const img = await resizeFile(file);
      const url = URL.createObjectURL(file);
      // Revoke the previous preview URL before replacing it.
      if (imagePreviewUrlRef.current) URL.revokeObjectURL(imagePreviewUrlRef.current);
      imagePreviewUrlRef.current = url;
      setImage(img);
      setImagePreview(url);
    } catch (e) {
      console.error('LiveThread image attach error', e);
    }
  };

  const handleFilesAttach = async (event) => {
    const arr = Array.from(event.target.files || []);
    // Reset the input so re-picking the same file fires onChange again.
    event.target.value = '';
    const rejected = [];
    const ok = arr.filter((f) => {
      if (!fileTypeOk(f) || !fileSizeOk(f)) {
        rejected.push(f.name);
        return false;
      }
      return true;
    });
    if (rejected.length > 0) {
      setFileError(`Archivos rechazados: ${rejected.join(', ')}`);
    } else {
      setFileError('');
    }
    if (!ok.length) return;
    setOptimizing((n) => n + 1);
    try {
      const optimized = [];
      for (const f of ok) {
        // eslint-disable-next-line no-await-in-loop
        const out = await optimizeFile(f);
        optimized.push(out);
      }
      setFiles((prev) => [...prev, ...optimized]);
    } catch (e) {
      console.error('LiveThread files attach optimize error', e);
      setFiles((prev) => [...prev, ...ok]);
    } finally {
      setOptimizing((n) => Math.max(0, n - 1));
    }
  };

  const removeFile = (idx) => setFiles((prev) => prev.filter((_, i) => i !== idx));
  const revokeImagePreviewUrl = () => {
    if (imagePreviewUrlRef.current) {
      URL.revokeObjectURL(imagePreviewUrlRef.current);
      imagePreviewUrlRef.current = null;
    }
  };
  const removeImage = () => {
    revokeImagePreviewUrl();
    setImage(null);
    setImagePreview(null);
  };

  const handleSubmit = () => {
    // Don't send while attachments are still being optimized — the files
    // would be silently dropped from the outgoing comment.
    if (optimizing > 0) return;
    // In-flight guard: a double-click while the parent's promise is pending
    // must not fire a second POST.
    if (submittingRef.current) return;
    const trimmed = (text || '').trim();
    if (trimmed === '' && !image && files.length === 0) return;
    const clearDraft = () => {
      setText('');
      setImage(null);
      revokeImagePreviewUrl();
      setImagePreview(null);
      setFiles([]);
      // Notify others that typing stopped
      if (stopTypingTimerRef.current) {
        clearTimeout(stopTypingTimerRef.current);
        stopTypingTimerRef.current = null;
      }
      sendTyping(false);
      lastTypingSentRef.current = 0;
    };
    if (typeof onSubmitComment === 'function') {
      // The parent returns a promise resolving to false on failure — in that
      // case the draft (text + attachments) stays so the user can retry.
      submittingRef.current = true;
      setSubmitting(true);
      Promise.resolve(onSubmitComment({ text: trimmed, image, files }))
        .then((ok) => { if (ok !== false) clearDraft(); })
        .catch(() => {})
        .finally(() => {
          submittingRef.current = false;
          setSubmitting(false);
        });
    } else {
      clearDraft();
    }
  };

  const attachmentBasename = (path) =>
    typeof path === 'string' ? path.split('/').pop() : String(path);

  // Stored adjunto paths are absolute (start with '/'); strip leading
  // slashes so the media URL doesn't get a double slash.
  const gestionMediaUrl = (path) =>
    `/api/gestiones/media/${String(path).replace(/^\/+/, '')}`;

  const IMAGE_ADJUNTO_RE = /\.(jpe?g|png|gif|webp)$/i;
  const isImageAdjunto = (path) => IMAGE_ADJUNTO_RE.test(attachmentBasename(path));

  return (
    <>
      {!isChat && (
        <>
          <Divider sx={{ mt: 1, mb: 1.5 }} />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7, mb: 1.5 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, flex: 1 }}>
              Comentarios
            </Typography>
          </Box>
        </>
      )}

      {list.length === 0 && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 2, textAlign: 'center' }}
        >
          No hay ningún comentario
        </Typography>
      )}

      <Box ref={listRef}>
        {list.map((comentario, index) => {
          const isMine = myEmail === comentario.usuario;
          return (
            <Box
              key={commentIdentity(comentario)}
              data-livethread-comment
              sx={{
                mb: 2,
                borderRadius: 1,
                px: highlightIndex === index ? 1 : 0,
                py: highlightIndex === index ? 0.5 : 0,
                transition: 'background-color 600ms ease-out',
                backgroundColor: highlightIndex === index
                  ? (isDark ? 'rgba(72,113,230,0.28)' : 'rgba(72,113,230,0.18)')
                  : 'transparent',
              }}
            >
            {isChat ? (
              <Box sx={{ display: 'flex', justifyContent: isMine ? 'flex-end' : 'flex-start' }}>
                <Box
                  sx={{
                    maxWidth: '75%',
                    px: 1.5,
                    py: 1,
                    borderRadius: 2,
                    backgroundColor: isMine ? 'secondary.main' : 'background.paper',
                    color: isMine ? '#fff' : 'text.primary',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
                  }}
                >
                  {!isMine && (
                    <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.25, opacity: 0.85 }}>
                      {comentario.usuario_nombre}
                      {formatEquipoLabel(comentario.equipo) && (
                        <Chip
                          label={formatEquipoLabel(comentario.equipo)}
                          size="small"
                          sx={{ ml: 0.5, height: 16, fontSize: '0.6rem' }}
                        />
                      )}
                    </Typography>
                  )}
                  {comentario.comentario && (
                    <Typography variant="body2" sx={{ lineHeight: 1.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {comentario.comentario}
                    </Typography>
                  )}
                  {/* Averias-style single attached image */}
                  {comentario.imagen_adjunta && imageBaseUrl && (
                    <Box sx={{ mt: 1, cursor: 'pointer' }}>
                      <img
                        src={`${imageBaseUrl}_${comentario.fecha}_cmnt.jpg`}
                        alt="Comment Image"
                        style={{
                          height: '120px',
                          objectFit: 'cover',
                          borderRadius: '8px',
                        }}
                        onClick={() =>
                          onOpenImage &&
                          onOpenImage(
                            `${imageBaseUrl}_${comentario.fecha}_cmnt.jpg`
                          )
                        }
                      />
                    </Box>
                  )}
                  {/* Gestiones-style file attachments: images render as
                      thumbnails that open the lightbox; other files render
                      as doc chips linking to the media endpoint. */}
                  {comentario.adjuntos && comentario.adjuntos.length > 0 && (
                    <Box
                      sx={{
                        mt: 1,
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 0.5,
                      }}
                    >
                      {comentario.adjuntos.map((path, ai) => (
                        isImageAdjunto(path) ? (
                          <img
                            key={typeof path === 'string' ? path : `catt-${index}-${ai}`}
                            src={gestionMediaUrl(path)}
                            alt={attachmentBasename(path)}
                            style={{
                              height: '120px',
                              objectFit: 'cover',
                              borderRadius: '8px',
                              cursor: 'pointer',
                            }}
                            onClick={() =>
                              onOpenImage && onOpenImage(gestionMediaUrl(path))
                            }
                          />
                        ) : (
                          <Chip
                            key={typeof path === 'string' ? path : `catt-${index}-${ai}`}
                            icon={<AttachFileIcon sx={{ fontSize: '0.9rem' }} />}
                            label={attachmentBasename(path)}
                            size="small"
                            component="a"
                            href={gestionMediaUrl(path)}
                            target="_blank"
                            rel="noopener noreferrer"
                            clickable
                            sx={{ height: 24, fontSize: '0.7rem', textDecoration: 'none' }}
                          />
                        )
                      ))}
                    </Box>
                  )}
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5, mt: 0.5 }}>
                    <Typography variant="caption" sx={{ fontSize: '0.65rem', opacity: 0.75 }}>
                      {AppConfig.formatFriendlyDate(comentario.fecha)}
                    </Typography>
                    {isMine && onDeleteComment && (
                      <IconButton
                        size="small"
                        onClick={() => onDeleteComment(comentario)}
                        sx={{ p: 0.2, color: isMine ? '#fff' : 'inherit' }}
                      >
                        <DeleteIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    )}
                  </Box>
                </Box>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                <Avatar
                  sx={{
                    width: 36,
                    height: 36,
                    fontSize: '0.85rem',
                    backgroundColor: 'primary.main',
                  }}
                >
                  {comentario.usuario_nombre
                    ? comentario.usuario_nombre.charAt(0).toUpperCase()
                    : 'U'}
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, display: 'flex', alignItems: 'center' }}
                    >
                      {comentario.usuario_nombre}
                      {formatEquipoLabel(comentario.equipo) && (
                        <Chip
                          label={formatEquipoLabel(comentario.equipo)}
                          size="small"
                          sx={{ ml: 0.5, height: 16, fontSize: '0.6rem' }}
                        />
                      )}
                    </Typography>
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        {AppConfig.formatFriendlyDate(comentario.fecha)}
                      </Typography>
                      {AuthService.getCurrentUserEmail() === comentario.usuario &&
                        onDeleteComment && (
                          <IconButton
                            size="small"
                            onClick={() => onDeleteComment(comentario)}
                            sx={{ p: 0.2 }}
                          >
                            <DeleteIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        )}
                    </Box>
                  </Box>
                  {comentario.comentario && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 0.3, lineHeight: 1.5 }}
                    >
                      {comentario.comentario}
                    </Typography>
                  )}
                  {/* Averias-style single attached image */}
                  {comentario.imagen_adjunta && imageBaseUrl && (
                    <Box sx={{ mt: 1, cursor: 'pointer' }}>
                      <img
                        src={`${imageBaseUrl}_${comentario.fecha}_cmnt.jpg`}
                        alt="Comment Image"
                        style={{
                          height: '60px',
                          objectFit: 'cover',
                          borderRadius: '8px',
                        }}
                        onClick={() =>
                          onOpenImage &&
                          onOpenImage(
                            `${imageBaseUrl}_${comentario.fecha}_cmnt.jpg`
                          )
                        }
                      />
                    </Box>
                  )}
                  {/* Gestiones-style file attachments: images render as
                      thumbnails that open the lightbox; other files render
                      as doc chips linking to the media endpoint. */}
                  {comentario.adjuntos && comentario.adjuntos.length > 0 && (
                    <Box
                      sx={{
                        mt: 1,
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 0.5,
                      }}
                    >
                      {comentario.adjuntos.map((path, ai) => (
                        isImageAdjunto(path) ? (
                          <img
                            key={typeof path === 'string' ? path : `catt-${index}-${ai}`}
                            src={gestionMediaUrl(path)}
                            alt={attachmentBasename(path)}
                            style={{
                              height: '60px',
                              objectFit: 'cover',
                              borderRadius: '8px',
                              cursor: 'pointer',
                            }}
                            onClick={() =>
                              onOpenImage && onOpenImage(gestionMediaUrl(path))
                            }
                          />
                        ) : (
                          <Chip
                            key={typeof path === 'string' ? path : `catt-${index}-${ai}`}
                            icon={<AttachFileIcon sx={{ fontSize: '0.9rem' }} />}
                            label={attachmentBasename(path)}
                            size="small"
                            component="a"
                            href={gestionMediaUrl(path)}
                            target="_blank"
                            rel="noopener noreferrer"
                            clickable
                            sx={{ height: 24, fontSize: '0.7rem', textDecoration: 'none' }}
                          />
                        )
                      ))}
                    </Box>
                  )}
                </Box>
              </Box>
            )}
            </Box>
          );
        })}
      </Box>

      {canComment && (
        <Box
          sx={isChat ? {
            // Chat variant: pin the composer to the bottom of the scrollable
            // thread (WhatsApp-style). Sticky keeps it visible while the list
            // scrolls; mt:'auto' anchors it to the bottom on short threads.
            // The mx:-2 bleed + opaque bg cover the thread container's px:2
            // gutters so messages don't peek past the bar while scrolling.
            position: 'sticky',
            bottom: 0,
            zIndex: 10,
            mt: 'auto',
            mx: -2,
            px: 2,
            backgroundColor: 'background.default',
          } : undefined}
        >
        {typingUser && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              mb: 0.5,
              px: 1,
            }}
          >
            <Box sx={{ display: 'flex', gap: 0.25 }}>
              <Box component="span" sx={typingDotSx(0)} />
              <Box component="span" sx={typingDotSx(1)} />
              <Box component="span" sx={typingDotSx(2)} />
            </Box>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontSize: '0.72rem', fontStyle: 'italic' }}
            >
              {typingUser.nombre} está escribiendo...
            </Typography>
          </Box>
        )}
        <Box
          sx={{
            mt: 1,
            display: 'flex',
            alignItems: 'flex-end',
            gap: 1,
            backgroundColor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            p: 1.5,
          }}
        >
          <Box sx={{ flex: 1 }}>
            <TextField
              placeholder="Escribir un comentario..."
              variant="standard"
              fullWidth
              multiline
              maxRows={3}
              onChange={handleTextChange}
              value={text}
              InputProps={{ disableUnderline: true }}
              sx={{
                '& .MuiInputBase-root': {
                  fontSize: '0.85rem',
                  color: 'text.primary',
                },
              }}
            />
            {imageMode && imagePreview && (
              <Box
                sx={{
                  mt: 1,
                  position: 'relative',
                  display: 'inline-block',
                }}
              >
                <img
                  src={imagePreview}
                  alt="Attached"
                  style={{ width: 32, height: 32 }}
                />
                <IconButton
                  size="small"
                  onClick={removeImage}
                  sx={{ ml: 0.5 }}
                >
                  <ClearIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Box>
            )}
            {!imageMode && files.length > 0 && (
              <Box
                sx={{
                  mt: 1,
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 0.5,
                }}
              >
                {files.map((f, idx) => (
                  <Chip
                    key={`cf-${idx}`}
                    icon={<AttachFileIcon sx={{ fontSize: '0.9rem' }} />}
                    label={f.name}
                    size="small"
                    onDelete={() => removeFile(idx)}
                    sx={{ height: 24, fontSize: '0.7rem' }}
                  />
                ))}
              </Box>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {imageMode ? (
              <>
                <input
                  accept="image/*"
                  style={{ display: 'none' }}
                  id="livethread-image"
                  type="file"
                  onChange={handleImageAttach}
                />
                {!imagePreview && (
                  <label htmlFor="livethread-image">
                    <IconButton
                      component="span"
                      size="small"
                      sx={{ color: 'primary.main' }}
                    >
                      <AddPhotoAlternateIcon fontSize="small" />
                    </IconButton>
                  </label>
                )}
              </>
            ) : (
              <>
                <input
                  accept={
                    (allowedFileTypes && allowedFileTypes.join(',')) || '*'
                  }
                  style={{ display: 'none' }}
                  id="livethread-files"
                  type="file"
                  multiple
                  onChange={handleFilesAttach}
                />
                <label htmlFor="livethread-files">
                  <IconButton
                    component="span"
                    size="small"
                    sx={{ color: 'primary.main' }}
                  >
                    <AttachFileIcon fontSize="small" />
                  </IconButton>
                </label>
              </>
            )}
            <IconButton
              size="small"
              onClick={handleSubmit}
              // Keep this enabled: MUI sets pointer-events:none on
              // .Mui-disabled, so while submitting/optimizing the wrapping
              // Box ends up absorbing the click (reported as a MuiBox-root
              // overlay intercepting the send button). handleSubmit already
              // guards both states — dim the icon for the same affordance.
              aria-disabled={optimizing > 0 || submitting}
              sx={{ color: 'primary.main', opacity: (optimizing > 0 || submitting) ? 0.4 : 1 }}
            >
              <SendIcon fontSize="small" />
            </IconButton>
          </Box>
          {fileError && (
            <Typography sx={{ fontSize: 11, color: 'error.main', mt: 0.5 }}>
              {fileError}
            </Typography>
          )}
        </Box>
        </Box>
      )}

      <style>{`@keyframes livethread-typing { 0%,60%,100%{opacity:0.3; transform:translateY(0)} 30%{opacity:1; transform:translateY(-3px)} }`}</style>
    </>
  );
}
